import { ContentMasterWorkspacePlatformView } from '../api/generated/models/ContentMasterWorkspacePlatformView';
import type { MarketplaceSyncStatusView } from '../api/generated/models/MarketplaceSyncStatusView';

/**
 * Marketplace codes the listing flow understands. Mirrors the
 * `marketplace` enum of `MarketplaceProductFieldsView` /
 * `POST /v2/api/marketplace-listing/batches`.
 */
export const MARKETPLACE_CODES = [
  'amazon',
  'otto',
  'kaufland',
  'metro',
  'cdiscount',
  'tiktok',
] as const;
export type MarketplaceCode = (typeof MARKETPLACE_CODES)[number];

export function isMarketplaceCode(value: unknown): value is MarketplaceCode {
  return typeof value === 'string' && (MARKETPLACE_CODES as readonly string[]).includes(value);
}

/** Human display name for a marketplace code (used in selectors / badges). */
export function marketplaceLabel(code: string | null | undefined): string {
  switch (code) {
    case 'amazon':
      return 'Amazon';
    case 'otto':
      return 'OTTO';
    case 'kaufland':
      return 'Kaufland';
    case 'metro':
      return 'METRO';
    case 'cdiscount':
      return 'Cdiscount';
    case 'tiktok':
      return 'TikTok';
    default:
      return code ?? '';
  }
}

/**
 * A channel is a "Marktplatz" sales channel when the backend tags it as such
 * (`nTypLabel === "Marktplatz"`), which is equivalent to `kind === "shop"` and
 * `nTyp === 6`. Only these channels are eligible for the marketplace-listing
 * flow - their `id` is always a `shop:N` source.
 */
export function isMarketplacePlatform(
  platform: ContentMasterWorkspacePlatformView | null | undefined
): boolean {
  if (!platform) {
    return false;
  }
  if ((platform.nTypLabel ?? '').trim() === 'Marktplatz') {
    return true;
  }
  return (
    platform.kind === ContentMasterWorkspacePlatformView.kind.SHOP && platform.nTyp === 6
  );
}

/** Find the workspace platform whose `id` matches `source`. */
export function findPlatformBySource(
  platforms: ContentMasterWorkspacePlatformView[] | undefined,
  source: string
): ContentMasterWorkspacePlatformView | null {
  if (!platforms?.length || !source) {
    return null;
  }
  return platforms.find((p) => p.id === source) ?? null;
}

/** True when `source` resolves to a Marktplatz channel in the workspace snapshot. */
export function isMarketplaceSource(
  platforms: ContentMasterWorkspacePlatformView[] | undefined,
  source: string
): boolean {
  return isMarketplacePlatform(findPlatformBySource(platforms, source));
}

/**
 * Single source of truth for the green/stale freshness verdict shown before a
 * listing batch. Treats the metadata as fresh only when BOTH the category-tree
 * sync and the channel-attributes sync are fresh, falling back to the
 * server-computed `overallFresh` when the granular flags are absent.
 *
 * `worker.alive` is intentionally NOT consulted here - per the contract it is
 * an informational heartbeat that does not gate freshness.
 */
export function isMarketplaceSyncFresh(status: MarketplaceSyncStatusView | null | undefined): boolean {
  if (!status) {
    return false;
  }
  const cat = status.categoriesSync?.fresh;
  const attr = status.attributesSync?.fresh;
  if (typeof cat === 'boolean' && typeof attr === 'boolean') {
    return cat && attr;
  }
  return status.overallFresh === true;
}

/**
 * True when a category is in scope but its attribute metadata has not been
 * pulled (`category.fresh === false` with `attributeCount === 0`) - the UI
 * surfaces a "category has no synced fields" hint in that case.
 */
export function isMarketplaceCategoryUnsynced(
  status: MarketplaceSyncStatusView | null | undefined
): boolean {
  const category = status?.category;
  if (!category) {
    return false;
  }
  return category.fresh === false && (category.attributeCount ?? 0) === 0;
}
